import { DataBaseNodes } from '@lib/firebase';
import { useSiteStore } from '@stores';
import { Site, SiteWithId, SiteWithUuid } from '@type';

import { Encryption } from '../../encryption-service';
import { CommunicationKeys, Preferences } from '../types';
import { BaseGateway } from './base.gateway';

export class SiteGateway extends BaseGateway {
  getSiteInfoByUuid = async (siteUuid: string): Promise<Site> => {
    const siteWithId = await this.sourceService.readDataOnce<SiteWithId>(
      `${DataBaseNodes.SitesData}/${siteUuid}`
    );
    if (!siteWithId) throw new Error('Site does not exist');

    return { ...siteWithId, siteUuid } as Site;
  };

  getSiteInfoById = async (siteId: number): Promise<Site> => {
    const siteWithUuid = await this.sourceService.readDataOnce<SiteWithUuid>(
      `${DataBaseNodes.Sites}/${siteId}`
    );

    if (!siteWithUuid) throw new Error('Site does not exist');

    return { ...siteWithUuid, siteId } as Site;
  };

  handShake = async (
    siteUuid: string,
    isFirstScan: boolean = false,
    isFromQrCode: boolean = false,
    isHandshakeWithoutPrivacyPolicyConsent: boolean = false
  ) => {
    const siteInfo = await this.getSiteInfoByUuid(siteUuid);

    const patientKeys = Encryption.createPairKeyForPatient();
    const sharedSecretKey = Encryption.getSharedSecretKey(
      patientKeys.patientKeys,
      siteInfo.publicKey
    );
    const hashedSharedKey = Encryption.generateHash(sharedSecretKey);

    const { updateSite } = useSiteStore.getState();

    updateSite({
      siteUuid: siteInfo.siteUuid,
      sharedSecretKey,
      hashedSharedKey,
    });

    const type = Preferences.Session.ExchangePubKey;
    const { ipAddress } = await this.utils.getPublicIpInformation();

    const payload = {
      preference: type,
      type,
      publicKey: patientKeys.publicKey as string,
      sharedSecretKey: hashedSharedKey,
      firstScan: isFirstScan,
      isFromPatientQrCode: isFromQrCode,
      siteUUID: siteUuid,
      isHandshakeWithoutPrivacyPolicyConsent,
      ipAddress,
      shouldRestorePatientComponents: false,
    };

    return await this.dataExchange<{
      siteUUID: string;
      isSSOLogin?: boolean;
      assetId?: number;
    }>({
      siteUuid: siteUuid,
      payload: payload,
      successKey: CommunicationKeys.HandShake.Done,
      failureKey: CommunicationKeys.HandShake.Exception,
    }).catch(error => {
      throw new Error(`Handshake has failed: ${JSON.stringify(error)}`);
    });
  };
}
